import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase = supabaseUrl && supabaseAnonKey 
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true
      }
    })
  : null;

if (!supabase) {
  console.error('❌ Supabase no inicializado. Verifica tus variables de entorno:');
  console.error('VITE_SUPABASE_URL:', supabaseUrl);
  console.error('VITE_SUPABASE_ANON_KEY:', supabaseAnonKey ? '(presente)' : '(faltante)');
} else {
  console.log('✅ Supabase inicializado correctamente');
}

// Funciones de autenticación
export const signInWithGoogle = async (options = {}) => {
  if (!supabase) return { error: 'Supabase no configurado' };
  
  // Usar la URL de producción o la actual si estamos en desarrollo
  const redirectUrl = window.location.hostname === 'localhost' 
    ? window.location.origin 
    : 'https://quiz-anatomia.pages.dev';
  
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: redirectUrl,
    },
  });
  return { data, error };
};

export const signOut = async () => {
  if (!supabase) return { error: 'Supabase no configurado' };
  
  const { error } = await supabase.auth.signOut();
  return { error };
};

export const getCurrentUser = async () => {
  if (!supabase) return { data: { user: null } };

  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.error('Error obteniendo sesión actual:', error);
      return { data: { user: null }, error };
    }

    const session = data?.session || null;
    const user = session?.user || null;
    return { data: { user }, error: null };
  } catch (err) {
    console.error('Excepción al obtener la sesión actual:', err);
    return { data: { user: null }, error: { message: err.message } };
  }
};

// Funciones de perfil
export const getProfile = async (userId) => {
  if (!supabase) return { data: null };
  
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  
  return { data, error };
};

export const updateProfile = async (userId, updates) => {
  if (!supabase) return { error: 'Supabase no configurado' };
  
  const { data, error } = await supabase
    .from('profiles')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', userId)
    .select()
    .single();
  
  return { data, error };
};

// Funciones de ranking
export const getRanking = async (limit = 10) => {
  if (!supabase) {
    return { data: [], error: 'Supabase no configurado' };
  }
  
  const { data, error } = await supabase
    .from('ranking_view')
    .select('*')
    .limit(limit);
  
  return { data: data || [], error };
};

// Funciones de historial
export const saveQuizHistory = async (userId, quizData) => {
  if (!supabase) {
    console.error('❌ Supabase no configurado - verifica las variables de entorno');
    return { error: { message: 'Supabase no configurado' } };
  }
  
  console.log('💾 Guardando historial del quiz:', { userId, quizData });
  
  try {
    const { data, error } = await supabase
      .from('quiz_history')
      .insert({
        user_id: userId,
        score: quizData.score,
        attempts: quizData.attempts,
        accuracy: quizData.accuracy,
        regions: quizData.regions,
        difficulties: quizData.difficulties,
        question_types: quizData.questionTypes,
      })
      .select();
    
    if (error) {
      console.error('❌ Error guardando historial:', error);
      console.error('   Código:', error.code);
      console.error('   Mensaje:', error.message);
      console.error('   Detalles:', error.details);
      console.error('   Hint:', error.hint);
      return { data: null, error };
    }
    
    console.log('✅ Historial guardado correctamente:', data);
    return { data: data?.[0], error: null };
  } catch (err) {
    console.error('❌ Error de red o excepción:', err);
    return { data: null, error: { message: err.message } };
  }
};

// Leer logros de usuario ya guardados (opcional, para marcarlos como desbloqueados)
export const getUserAchievements = async (userId) => {
  if (!supabase) return { data: [], error: { message: 'Supabase no configurado' } };

  try {
    const { data, error } = await supabase
      .from('user_achievements')
      .select('achievement_id')
      .eq('user_id', userId);

    if (error) {
      console.error('❌ Error leyendo logros de usuario:', error);
      return { data: [], error };
    }

    const ids = (data || []).map((row) => row.achievement_id);
    return { data: ids, error: null };
  } catch (err) {
    console.error('❌ Error de red al leer logros de usuario:', err);
    return { data: [], error: { message: err.message } };
  }
};

// Guardar historial por región
export const saveRegionHistory = async (userId, regionStats) => {
  if (!supabase) {
    console.error('❌ Supabase no configurado - saveRegionHistory');
    return { error: { message: 'Supabase no configurado' } };
  }

  if (!regionStats || Object.keys(regionStats).length === 0) {
    console.log('ℹ️ saveRegionHistory: no hay datos de región para guardar');
    return { error: null };
  }

  const rows = Object.entries(regionStats).map(([region, data]) => ({
    user_id: userId,
    region,
    correct: data.correct || 0,
    incorrect: data.incorrect || 0,
    total: data.total || (data.correct || 0) + (data.incorrect || 0)
  }));

  console.log('💾 Guardando region_history:', rows);

  try {
    const { data, error } = await supabase
      .from('region_history')
      .insert(rows)
      .select();

    if (error) {
      console.error('❌ Error guardando region_history:', error);
      return { data: null, error };
    }

    console.log('✅ region_history guardado correctamente');
    return { data, error: null };
  } catch (err) {
    console.error('❌ Error de red al guardar region_history:', err);
    return { data: null, error: { message: err.message } };
  }
};

// Leer historial por región agregado por usuario
export const getRegionHistory = async (userId) => {
  if (!supabase) return { data: [], error: { message: 'Supabase no configurado' } };

  try {
    const { data, error } = await supabase
      .from('region_history')
      .select('*')
      .eq('user_id', userId);

    return { data: data || [], error };
  } catch (err) {
    console.error('❌ Error de red al leer region_history:', err);
    return { data: [], error: { message: err.message } };
  }
};

// Guardar logro desbloqueado
export const saveAchievement = async (userId, achievementId) => {
  if (!supabase) {
    console.error('❌ Supabase no configurado - verifica las variables de entorno');
    return { error: { message: 'Supabase no configurado' } };
  }

  console.log('🏆 Guardando logro:', { userId, achievementId });

  try {
    // Evitar duplicados: upsert por clave compuesta (user_id, achievement_id)
    const { data, error } = await supabase
      .from('user_achievements')
      .upsert(
        { user_id: userId, achievement_id: achievementId },
        { onConflict: 'user_id,achievement_id' }
      )
      .select();

    if (error) {
      console.error('❌ Error guardando logro:', error);
      console.error('   Código:', error.code);
      console.error('   Mensaje:', error.message);
      console.error('   Detalles:', error.details);
      console.error('   Hint:', error.hint);
      return { data: null, error };
    }

    console.log('✅ Logro guardado correctamente:', data);
    return { data: data?.[0], error: null };
  } catch (err) {
    console.error('❌ Error de red o excepción:', err);
    return { data: null, error: { message: err.message } };
  }
};

export const getQuizHistory = async (userId, limit = 20) => {
  if (!supabase) return { data: [] };
  
  const { data, error } = await supabase
    .from('quiz_history')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);
  
  return { data, error };
};

// Feed global de quizzes (actividad social de todos los usuarios)
export const getGlobalQuizFeed = async (limit = 30) => {
  if (!supabase) return { data: [], error: { message: 'Supabase no configurado' } };

  try {
    const { data, error } = await supabase
      .from('quiz_history')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    return { data: data || [], error };
  } catch (err) {
    console.error('❌ Error de red al leer feed global de quizzes:', err);
    return { data: [], error: { message: err.message } };
  }
};

// Crear un post social (texto + URLs de media opcionales)
export const createSocialPost = async (userId, { content, imageUrls = [], audioUrls = [] }) => {
  if (!supabase) return { data: null, error: { message: 'Supabase no configurado' } };

  try {
    const { data: postData, error: postError } = await supabase
      .from('social_posts')
      .insert({
        user_id: userId,
        content: content || null,
        has_image: imageUrls.length > 0,
        has_audio: audioUrls.length > 0,
      })
      .select()
      .single();

    if (postError) return { data: null, error: postError };

    const mediaRows = [
      ...imageUrls.map((url) => ({ post_id: postData.id, media_type: 'image', url })),
      ...audioUrls.map((url) => ({ post_id: postData.id, media_type: 'audio', url })),
    ];

    if (mediaRows.length > 0) {
      const { error: mediaError } = await supabase
        .from('social_post_media')
        .insert(mediaRows);

      if (mediaError) {
        console.error('Error insertando media del post:', mediaError);
      }
    }

    return { data: postData, error: null };
  } catch (err) {
    console.error('Error en createSocialPost:', err);
    return { data: null, error: { message: err.message } };
  }
};

// Actualizar un post social (solo campos sencillos como content)
export const updateSocialPost = async (postId, updates) => {
  if (!supabase) return { data: null, error: { message: 'Supabase no configurado' } };

  try {
    const { data, error } = await supabase
      .from('social_posts')
      .update({ ...updates })
      .eq('id', postId)
      .select()
      .single();

    return { data, error };
  } catch (err) {
    console.error('Error en updateSocialPost:', err);
    return { data: null, error: { message: err.message } };
  }
};

// Eliminar un post social
export const deleteSocialPost = async (postId) => {
  if (!supabase) return { error: { message: 'Supabase no configurado' } };

  try {
    const { error } = await supabase
      .from('social_posts')
      .delete()
      .eq('id', postId);

    return { error };
  } catch (err) {
    console.error('Error en deleteSocialPost:', err);
    return { error: { message: err.message } };
  }
};

// Obtener feed de posts sociales con media, respuestas y reacciones básicas
export const getSocialPosts = async (limit = 30) => {
  if (!supabase) return { data: [], error: { message: 'Supabase no configurado' } };

  try {
    const { data, error } = await supabase
      .from('social_posts')
      .select(`
        id,
        user_id,
        content,
        has_image,
        has_audio,
        created_at,
        social_post_media ( id, media_type, url ),
        social_post_replies ( id, user_id, content, created_at ),
        social_post_reactions ( id, user_id, created_at )
      `)
      .order('created_at', { ascending: false })
      .limit(limit);

    return { data: data || [], error };
  } catch (err) {
    console.error('Error en getSocialPosts:', err);
    return { data: [], error: { message: err.message } };
  }
};

// Obtener replies de un post (por si quieres recargar solo comentarios)
export const getPostReplies = async (postId) => {
  if (!supabase) return { data: [], error: { message: 'Supabase no configurado' } };

  try {
    const { data, error } = await supabase
      .from('social_post_replies')
      .select('*')
      .eq('post_id', postId)
      .order('created_at', { ascending: true });

    return { data: data || [], error };
  } catch (err) {
    console.error('Error en getPostReplies:', err);
    return { data: [], error: { message: err.message } };
  }
};

// Añadir una reply a un post
export const addPostReply = async (userId, postId, content) => {
  if (!supabase) return { data: null, error: { message: 'Supabase no configurado' } };

  try {
    const { data, error } = await supabase
      .from('social_post_replies')
      .insert({
        post_id: postId,
        user_id: userId,
        content,
      })
      .select()
      .single();

    return { data, error };
  } catch (err) {
    console.error('Error en addPostReply:', err);
    return { data: null, error: { message: err.message } };
  }
};

// Obtener resumen de corazones por post (conteo y si el usuario actual reaccionó)
export const getPostReactionsSummary = async (postIds, currentUserId) => {
  if (!supabase || !postIds || postIds.length === 0) return { data: { counts: {}, userHasReacted: {} }, error: null };

  try {
    const { data, error } = await supabase
      .from('social_post_reactions')
      .select('post_id, user_id')
      .in('post_id', postIds);

    if (error) return { data: { counts: {}, userHasReacted: {} }, error };

    const counts = {};
    const userHasReacted = {};

    (data || []).forEach((row) => {
      counts[row.post_id] = (counts[row.post_id] || 0) + 1;
      if (currentUserId && row.user_id === currentUserId) {
        userHasReacted[row.post_id] = true;
      }
    });

    return { data: { counts, userHasReacted }, error: null };
  } catch (err) {
    console.error('Error en getPostReactionsSummary:', err);
    return { data: { counts: {}, userHasReacted: {} }, error: { message: err.message } };
  }
};

// Alternar corazón en un post (like/unlike)
export const togglePostHeart = async (userId, postId) => {
  if (!supabase) return { liked: false, error: { message: 'Supabase no configurado' } };

  try {
    // Comprobar si ya existe reacción
    const { data: existing, error: existingError } = await supabase
      .from('social_post_reactions')
      .select('id')
      .eq('user_id', userId)
      .eq('post_id', postId)
      .eq('reaction_type', 'heart')
      .maybeSingle();

    if (existingError && existingError.code !== 'PGRST116') {
      // PGRST116 = no rows
      console.error('Error comprobando reacción existente:', existingError);
    }

    if (existing) {
      // Ya existe: eliminar (unlike)
      const { error: deleteError } = await supabase
        .from('social_post_reactions')
        .delete()
        .eq('id', existing.id);

      if (deleteError) {
        console.error('Error eliminando reacción:', deleteError);
        return { liked: true, error: deleteError };
      }

      return { liked: false, error: null };
    }

    // No existe: insertar (like)
    const { error: insertError } = await supabase
      .from('social_post_reactions')
      .insert({
        post_id: postId,
        user_id: userId,
        reaction_type: 'heart',
      });

    if (insertError) {
      console.error('Error insertando reacción:', insertError);
      return { liked: false, error: insertError };
    }

    return { liked: true, error: null };
  } catch (err) {
    console.error('Error en togglePostHeart:', err);
    return { liked: false, error: { message: err.message } };
  }
};

// Subir archivo de media social a Storage y devolver URL pública
export const uploadSocialMedia = async (file, type) => {
  if (!supabase) return { url: null, error: { message: 'Supabase no configurado' } };

  const bucket = type === 'image' ? 'social-images' : 'social-audio';
  const ext = file.name.split('.').pop() || '';
  const fileName = `${crypto.randomUUID()}.${ext}`;
  const filePath = `${type}s/${fileName}`;

  try {
    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      console.error('Error subiendo archivo a Storage:', uploadError);
      return { url: null, error: uploadError };
    }

    const { data } = supabase.storage.from(bucket).getPublicUrl(filePath);
    const publicUrl = data?.publicUrl || null;

    return { url: publicUrl, error: null };
  } catch (err) {
    console.error('Error de red en uploadSocialMedia:', err);
    return { url: null, error: { message: err.message } };
  }
};
